import { AddRounded, DeleteRounded, OpenInNewRounded } from '@mui/icons-material'
import { Autocomplete, Box, CircularProgress, IconButton, Stack, TextField, Tooltip } from '@mui/material'
import { DataGrid, GridActionsCellItem, GridCellParams, GridColDef, GridRenderCellParams, GridTreeNode, useGridApiRef } from '@mui/x-data-grid'
import { memo, useCallback, useMemo, useState } from 'react'
import FlowApiClient from '../../../../backend/sub_api_clients/FlowApiClient'
import { IFormField } from '../../../../definitions/FormField'
import { IFormGroup } from '../../../../definitions/FormGroup'
import { IFlow, INode } from '../../../../definitions/Nodes'
import { IAcceptedData } from '../../../../definitions/NodesDescription'
import Helper from '../../../../helpers/Helper'
import useQuery, { buildQueryKey } from '../../../hooks/useQuery'

interface P {
    acceptedData: IAcceptedData<'ds_contract'>
    selectedNode: INode<'ds_contract'>
    flow: IFlow
    setSelected: (node: INode<'ds_contract'>) => void
    disable: boolean
}

type IContractDataProp = INode<'ds_contract'>['data']['contractData']['properties'][0]

const NodeContractEditor = ({ selectedNode, setSelected, disable }: P) => {
    const [selectedSchemaUUID, setSelectedSchemaUUID] = useState(selectedNode?.data?.contractData?.schemaUUID ?? null)
    const [rows, setRows] = useState<IContractDataProp[]>(selectedNode?.data?.contractData?.properties ?? [])
    const apiRef = useGridApiRef()

    const { status, response } = useQuery({
        queryKey: buildQueryKey('getAllSchemaForContractNode'),
        queryFn: () => FlowApiClient.getAllSchemaForContractNode(),
        cache: false
    })

    const deleteRow = useCallback((id: string) => {
        setRows(prev => prev.filter(r => r.id !== id))
        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                contractData: {
                    ...selectedNode.data.contractData,
                    properties: selectedNode.data.contractData.properties.filter(p => p.id !== id)
                }
            }
        })
    }, [selectedNode?.data?.contractData?.properties])

    const addRow = useCallback(() => {
        setRows(prev => {
            const newRow = { id: Helper.generateUUID(), key: '', outputName: '' }
            setSelected({
                ...selectedNode,
                data: {
                    ...selectedNode.data,
                    contractData: {
                        ...selectedNode.data.contractData,
                        properties: [...(selectedNode?.data?.contractData?.properties ?? []), newRow]
                    }
                }
            })
            const updated = [...prev, newRow]

            setTimeout(() => {
                const index = updated.length - 1
                apiRef.current?.scrollToIndexes({ rowIndex: index })
            }, 0)

            return updated
        })
    }, [apiRef, selectedNode?.data?.contractData?.properties])

    const processRowUpdate = useCallback((newRow: IContractDataProp, oldRow: IContractDataProp) => {
        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                contractData: {
                    ...selectedNode.data.contractData,
                    properties: (selectedNode?.data?.contractData?.properties ?? []).map(r => r.id === oldRow.id ? newRow : r)
                }
            }
        })
        setRows(prev => prev.map(r => r.id === oldRow.id ? newRow : r))
        return newRow
    }, [selectedNode?.data?.contractData?.properties])

    const getCellClassName = useCallback((params: GridCellParams<any, any, any, GridTreeNode>) => {
        if (params.field === 'key' && !params.row.key) return 'error-cell'
        if (params.field === 'outputName' && !params.row.outputName) return 'error-cell'
        return ''
    }, [])

    const handleOpenSchema = useCallback(() => window.open(`${window.location.origin}/contracts/contract-schema/contract-schema-editor?uuid=${selectedSchemaUUID}`, '_blank'), [selectedSchemaUUID])

    const columns: GridColDef[] = useMemo(() => {
        const valueOptions = extractKeys(response?.find(r => r.uuid === selectedSchemaUUID)?.steps ?? []).sort((a, b) => a.localeCompare(b))
        return [
            {
                field: 'key',
                headerName: 'Field name',
                flex: 1,
                type: 'singleSelect',
                valueOptions,
                editable: true,
                renderCell: withErrorTooltip(params => Boolean(params.row.key), 'Double click to choose an option')
            },
            {
                field: 'outputName',
                headerName: 'Custom name',
                flex: 1,
                editable: true,
                renderCell: withErrorTooltip(params => Boolean(params.row.outputName), 'Double click to type a custom name')
            },
            {
                field: 'actions',
                type: 'actions',
                headerName: 'Action',
                flex: 0.3,
                getActions: params => [
                    <GridActionsCellItem
                        key={params.row.id}
                        icon={<DeleteRounded color='error' />}
                        label='Delete'
                        disabled={disable}
                        onClick={() => deleteRow(params.row.id as string)}
                    />
                ]
            }
        ]
    }, [response, selectedSchemaUUID, deleteRow])

    const schemaOptions = useMemo(() => {
        const r = response ?? []
        return r.map(r => ({
            uuid: r.uuid,
            name: r.name
        }))
    }, [response])

    const isLoading = status === 'loading' || status === 'stale'

    const hasInvalidRows = rows.some(r => !r.key || !r.outputName)

    return (
        <Stack spacing={3}>
            <Stack flexDirection='row' alignItems='center' gap={1}>
                <Autocomplete
                    disabled={disable}
                    size='small'
                    fullWidth
                    options={schemaOptions}
                    getOptionLabel={option => option.name}
                    isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                    value={schemaOptions?.find(s => s.uuid === selectedSchemaUUID) ?? null}
                    loading={isLoading}
                    onChange={(_, selected) => {
                        setSelectedSchemaUUID(selected?.uuid ?? null)
                        setSelected({
                            ...selectedNode,
                            data: {
                                ...selectedNode.data,
                                contractData: {
                                    ...selectedNode.data.contractData,
                                    schemaUUID: selected?.uuid ?? null
                                }
                            }
                        })
                    }}
                    renderOption={(props, option) => {
                        return (
                            <li {...props} key={option.uuid}>
                                {option.name}
                            </li>
                        )
                    }}

                    renderInput={params => (
                        <TextField
                            {...params}
                            label='Select a schema'
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {isLoading && <CircularProgress color='inherit' size={18} />}
                                        {params.InputProps.endAdornment}
                                    </>
                                )
                            }}
                        />
                    )}
                />
                <IconButton onClick={handleOpenSchema} disabled={!selectedSchemaUUID}>
                    <OpenInNewRounded />
                </IconButton>
            </Stack>
            <Stack>
                <DataGrid
                    apiRef={apiRef}
                    getCellClassName={getCellClassName}
                    rows={rows}
                    columns={columns}
                    editMode='row'
                    isCellEditable={() => !disable}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={err => console.error(err)}
                    disableRowSelectionOnClick
                    hideFooter
                    sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                        },
                        'opacity': disable ? 0.6 : 1,
                        '& .error-cell': {
                            border: theme => `1px solid ${theme.palette.error.main}`
                        }
                    }}
                />
                <Stack width='100%'>
                    <IconButton
                        color='primary'
                        size='small'
                        disabled={disable || hasInvalidRows}
                        onClick={addRow}
                        sx={{ borderRadius: 1 }}>
                        <AddRounded />
                    </IconButton>
                </Stack>
            </Stack>
        </Stack>
    )
}

export default memo(NodeContractEditor)

function extractKeys(steps: IFormGroup[]): string[] {
    const keys: string[] = []

    function walk(node: IFormField) {
        if (!node || typeof node !== 'object') return

        if (node.key) {
            keys.push(node.key)
        }

        if (node.type === 'list') {
            return
        }

        if (Array.isArray(node.fields)) {
            node.fields.forEach(walk)
        }

        if (Array.isArray(node.objectFields)) {
            node.objectFields.forEach(walk)
        }

        if (Array.isArray(node.childFields)) {
            node.childFields.forEach(walk)
        }
    }

    steps.forEach(section => {
        if (Array.isArray(section.fields)) {
            section.fields.forEach(walk)
        }
    })

    return keys
}

function withErrorTooltip(getIsValid: (params: GridRenderCellParams) => boolean, message: string) {
    function RenderWithErrorTooltip(params: GridRenderCellParams) {
        const isValid = getIsValid(params)

        return (
            <Tooltip
                disableHoverListener={isValid}
                title={message}
                arrow
                componentsProps={{
                    tooltip: {
                        sx: {
                            bgcolor: theme => theme.palette.error.main
                        }
                    },
                    arrow: {
                        sx: {
                            color: theme => theme.palette.error.main
                        }
                    }
                }}>
                <Box height='100%' display='flex' alignItems='center'>
                    {params.value ?? ''}
                </Box>
            </Tooltip>
        )
    }

    return RenderWithErrorTooltip
}
